import { CurrencyCodeSchema } from 'types/currency';
import { z } from 'zod/v4';

const ProductLineInputSchema = z.object({
  id: z.coerce.number().optional(),
  quantity: z.coerce.number(),
  discount_rate: z.coerce.number().optional().default(0),
  current_unity_price_htva: z.coerce.number(),
  total_htva: z.coerce.number(),
  total_tvac: z.coerce.number(),
  product_id: z.coerce.number().optional(),
  // Credit-note "Ajouter une facture" lines: stripping this collapses the
  // backend's invoice-line override and zeroes VAT on the saved credit note.
  linked_invoice_id: z.coerce.number().optional(),
  product_attributes: z
    .object({
      name: z.string(),
      price_htva: z.coerce.number(),
      tax_category_code: z.string(),
    })
    .optional(),
});

const InvoiceBaseParamsSchema = z.object({
  debtor_id: z.coerce.number(),
  issue_date: z.string(),
  due_date: z.string(),
  comment: z.string().nullable().optional(),
  order_reference: z.string().nullable().optional(),
  billing_log_id: z.union([z.number(), z.string()]),
  currency: CurrencyCodeSchema.optional(),
  custom_variables: z.record(z.string(), z.string()).optional(),
});

export const InvoiceCreateParamsSchema = InvoiceBaseParamsSchema.extend({
  product_lines_attributes: z.array(ProductLineInputSchema.omit({ id: true })),
});

export type InvoiceCreateParams = z.infer<typeof InvoiceCreateParamsSchema>;

export const InvoiceUpdateParamsSchema = InvoiceBaseParamsSchema.partial().extend({
  product_lines_attributes: z.array(ProductLineInputSchema.omit({ id: true })).optional(),
});

export type InvoiceUpdateParams = z.infer<typeof InvoiceUpdateParamsSchema>;

// Credit notes share the invoice-form payload shape; the boundary schemas
// mirror the invoice ones, omitting line-level `id` on create/update so
// duplicated source-invoice ids and any unknown keys (notably circular
// React SyntheticEvent props that would crash axios's JSON.stringify) are
// stripped before the HTTP call.
export const CreditNoteCreateParamsSchema = InvoiceBaseParamsSchema.extend({
  product_lines_attributes: z.array(ProductLineInputSchema.omit({ id: true })),
});

export type CreditNoteCreateParams = z.infer<typeof CreditNoteCreateParamsSchema>;

export const CreditNoteUpdateParamsSchema = InvoiceBaseParamsSchema.partial().extend({
  product_lines_attributes: z.array(ProductLineInputSchema.omit({ id: true })).optional(),
});

export type CreditNoteUpdateParams = z.infer<typeof CreditNoteUpdateParamsSchema>;

const InvoiceAttributesSchema = z.looseObject({
  reference: z.string().nullable(),
  issue_date: z.string(),
  due_date: z.string().nullable(),
  comment: z.string().nullable(),
  total_htva: z.coerce.number(),
  total_tvac: z.coerce.number(),
  remaining_balance: z.coerce.number().nullable(),
  currency: CurrencyCodeSchema,
  lifecycle_state: z.string(),
  debtor_id: z.number(),
  status: z.string(),
});

export const InvoiceResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.string(),
    attributes: InvoiceAttributesSchema,
  }),
});

export type InvoiceResponse = z.infer<typeof InvoiceResponseSchema>;
